import React from 'react';
import { Link } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, MapPin, Clock, ArrowRight, ImageIcon, ShieldCheck,
  Calendar, Users, GraduationCap, Ticket,
} from 'lucide-react';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 })}`;

const TYPE = {
  single: { label: 'Evento', icon: Calendar },
  group: { label: 'Gruppo', icon: Users },
  course: { label: 'Corso', icon: GraduationCap },
};

function ExpCard({ e }) {
  const tm = TYPE[e.type] || TYPE.single;
  const TypeIcon = tm.icon;
  return (
    <Link
      to={`/esperienze/${e.slug || e.id}`}
      className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`storefront-exp-${e.id}`}
    >
      <div className="relative h-48 overflow-hidden bg-muted">
        {e.cover_image ? (
          <img src={mediaUrl(e.cover_image)} alt={e.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-9 w-9 text-muted-foreground" /></div>
        )}
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground shadow-sm backdrop-blur"><TypeIcon className="h-3 w-3 text-primary" />{tm.label}</span>
        <span className="absolute right-3 top-3 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-foreground backdrop-blur">da {euro(e.from_price)}</span>
      </div>
      <div className="p-5">
        {e.category && <span className="text-xs font-medium uppercase tracking-wide text-primary">{e.category}</span>}
        <h3 className="mt-1 font-display text-lg font-semibold text-foreground line-clamp-1">{e.title}</h3>
        {e.short_desc && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{e.short_desc}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {e.location?.name && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{e.location.name}</span>}
          {e.duration_min > 0 && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />{e.duration_min} min</span>}
        </div>
        <div className="mt-4 flex items-center justify-end border-t border-border pt-3">
          <span className="flex items-center gap-1 text-sm font-medium text-primary">Scopri e prenota <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
        </div>
      </div>
    </Link>
  );
}

/**
 * Vista pubblica della vetrina (live page) di un organizzatore di esperienze.
 * Riutilizzata sia nell'anteprima dell'editor (publicMode=false) sia nella pagina pubblica.
 */
export function ExperienceStorefrontView({ data, publicMode = true }) {
  const hero = data?.hero_image || (data?.experiences?.[0]?.cover_image) || '';
  const gallery = (data?.gallery || []).filter(Boolean);
  const exps = data?.experiences || [];

  return (
    <div data-testid="experience-storefront-view">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-3xl bg-muted">
        <div className="aspect-[16/9] w-full sm:aspect-[21/9]">
          {hero ? (
            <img src={mediaUrl(hero)} alt={data?.name || 'Organizzatore'} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5"><Sparkles className="h-12 w-12 text-primary/50" /></div>
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
          <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" />Organizzatore Esperienze
          </span>
          <h1 className="font-display text-2xl font-bold tracking-tight text-white sm:text-4xl">{data?.name || 'Organizzatore'}</h1>
          {data?.tagline && <p className="mt-1.5 max-w-2xl text-sm text-white/85 sm:text-base">{data.tagline}</p>}
          {data?.managed_by_team && (
            <Badge className="mt-3 gap-1 bg-white/90 text-primary hover:bg-white"><ShieldCheck className="h-3 w-3" />Gestito dal team Sila</Badge>
          )}
        </div>
      </div>

      {/* STORY */}
      {data?.story && (
        <section className="mt-8">
          <h2 className="font-display text-xl font-semibold text-foreground">La nostra storia</h2>
          <p className="mt-3 max-w-3xl whitespace-pre-line leading-relaxed text-muted-foreground">{data.story}</p>
        </section>
      )}

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl font-semibold text-foreground">Immagini</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {gallery.map((g, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-muted">
                <img src={mediaUrl(g)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ESPERIENZE */}
      <section className="mt-10">
        <div className="flex items-end justify-between gap-3">
          <h2 className="font-display text-xl font-semibold text-foreground">Le esperienze</h2>
          <span className="text-sm text-muted-foreground">{exps.length} pubblicate</span>
        </div>
        {exps.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center">
            <Ticket className="mx-auto h-9 w-9 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              {publicMode ? 'Nessuna esperienza pubblicata al momento.' : 'Pubblica un\u2019esperienza per vederla comparire qui.'}
            </p>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {exps.map((e) => <ExpCard key={e.id} e={e} />)}
          </div>
        )}
      </section>
    </div>
  );
}
